package top.wcpe.allincore.ui.runtime.element

import top.wcpe.allincore.layout.engine.LayoutMode
import kotlin.jvm.JvmStatic
import kotlin.math.max

/**
 * 折叠头部容器：第一个子是 header，第二个子是 body（[ScrollableContainer]）。
 * 当 body 向上滚动时，header 高度从 [expandedHeight] 按比例渐变到 [collapsedHeight]。
 *
 * 折叠进度 = clamp(body.scrollY / (expandedHeight - collapsedHeight), 0, 1)。
 * [currentHeaderHeight] 据此插值。pinned=true 时折叠到 collapsedHeight 后固定；
 * false 时（本 MVP 仍固定在 collapsedHeight，继续滚出留待后续）。
 *
 * 列式布局：header 在上、body 在下。渲染端按 [currentHeaderHeight] 设定 header 高度。
 */
class CollapsingHeader private constructor() : UIElement<CollapsingHeader>() {

    // 字段名与同名访问器方法冲突，backing 改名加后缀
    private var expandedHeightPx = DEFAULT_EXPANDED_HEIGHT
    private var collapsedHeightPx = DEFAULT_COLLAPSED_HEIGHT
    private var pinnedFlag = true

    init {
        styleField.layout().mode = LayoutMode.COLUMN
    }

    override fun self(): CollapsingHeader = this

    // -------- 字段 --------

    fun expandedHeight(px: Float): CollapsingHeader {
        expandedHeightPx = max(0f, px)
        return this
    }

    fun expandedHeight(): Float = expandedHeightPx

    fun collapsedHeight(px: Float): CollapsingHeader {
        collapsedHeightPx = max(0f, px)
        return this
    }

    fun collapsedHeight(): Float = collapsedHeightPx

    fun pinned(p: Boolean): CollapsingHeader {
        pinnedFlag = p
        return this
    }

    fun pinned(): Boolean = pinnedFlag

    // -------- 折叠计算 --------

    /** body 子元素（最后一个滚动容器子）；不存在或非滚动容器时返回 null。 */
    fun body(): ScrollableContainer<*>? =
        children().lastOrNull { it is ScrollableContainer<*> } as? ScrollableContainer<*>

    /** 折叠进度 [0,1]：0=完全展开，1=完全折叠。 */
    fun collapseProgress(): Float {
        val b = body() ?: return 0f
        val range = expandedHeightPx - collapsedHeightPx
        if (range <= 0f) {
            return 0f
        }
        return (b.scrollY() / range).coerceIn(0f, 1f)
    }

    /** 当前 header 高度：在 expanded 与 collapsed 之间按折叠进度插值。 */
    fun currentHeaderHeight(): Float {
        val p = collapseProgress()
        return expandedHeightPx + (collapsedHeightPx - expandedHeightPx) * p
    }

    companion object {
        /** 展开态 header 默认高度（像素）。 */
        private const val DEFAULT_EXPANDED_HEIGHT = 120f

        /** 折叠态 header 默认高度（像素）。 */
        private const val DEFAULT_COLLAPSED_HEIGHT = 24f

        @JvmStatic
        fun create(): CollapsingHeader = CollapsingHeader()
    }
}
